import React from "react";
import { ArrowDown } from "lucide-react";
import FlowSteps from "./FlowSteps";
import {
  SCROLL_OFFSET,
  H2,
  BODY,
  EYEBROW,
  CONTAINER,
  COL_LEFT,
  COL_RIGHT,
} from "./sections";

const TRADITIONAL_FLOW = [
  { label: "Speak" },
  { label: "Transcribe" },
  { label: "Edit by hand" },
];

const AI_FLOW = [
  { label: "Speak" },
  { label: "Transcribe" },
  { label: "Refine" },
  { label: "Format" },
  { label: "Use", solid: true },
];

const WritingWorkflowSection = () => {
  return (
    <section
      id="writing-workflow"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full"
    >
      <div
        className={`${CONTAINER} pt-[64px] pb-[64px] md:pt-[88px] md:pb-[88px] lg:pt-[112px] lg:pb-[112px]`}
      >
        <div className="flex flex-col lg:flex-row lg:items-start gap-[32px] lg:gap-[58px]">
          {/* Copy */}
          <div className={COL_LEFT}>
            <h2 className={H2}>
              How Zero Typing AI Fits Into Your Writing Workflow
            </h2>

            <div className="mt-[25.76px] flex flex-col gap-[25.76px]">
              <p className={BODY}>
                One of the main advantages of Zero Typing AI is that you
                don&apos;t have to treat voice typing as a separate writing
                activity.
              </p>
              <p className={BODY}>
                You can place your cursor where you want to enter text, speak
                your thoughts, and use AI-powered features to refine the result,
                or leave it untouched if you&apos;d rather work with the raw
                transcript.
              </p>
              <p className={BODY}>
                This makes voice input useful for more than simply replacing
                keyboard typing.
              </p>
            </div>
          </div>

          {/* Example card + the two pipelines */}
          <div className={COL_RIGHT}>
            <article className="rounded-[17.17px] border-[1.34px] border-[#3AB1F633] bg-[#EBF7FE] p-[16px] sm:p-[21.46px]">
              <p className={`${EYEBROW} flex items-center gap-[8px] text-[#0A0D1699]`}>
                <span
                  aria-hidden="true"
                  className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#3AB1F6]"
                />
                Writing an email
              </p>

              <blockquote className="mt-[12.88px] border-l-[2px] border-[#3AB1F64D] pl-[12.88px]">
                <p className="font-inter text-[13.5px] font-normal leading-[24px] tracking-[0px] text-[#0A0D1699]">
                  &ldquo;Hi Sarah! Wanted To Follow Up Regarding The Proposal We
                  Discussed Yesterday...&rdquo;
                </p>
              </blockquote>

              <p className={`${EYEBROW} mt-[14px] flex items-center gap-[6px] text-[#3AB1F6]`}>
                <ArrowDown
                  className="h-[13px] w-[13px] shrink-0"
                  strokeWidth={2.2}
                  aria-hidden="true"
                />
                Refined With One Tap
              </p>

              <p className="mt-[12.88px] font-jakarta text-[16.1px] font-medium leading-[26.16px] tracking-[0px] text-[#0A0D16]">
                Speak it once, then improve grammar, adjust the wording,
                reorganize, or switch style. The same flow works for notes,
                messages, reports, and documents.
              </p>
            </article>

            {/* Two ways to get there */}
            <div className="mt-[25.76px]">
              <p className={`${EYEBROW} flex items-center gap-[10px] text-[#3AB1F6]`}>
                <span
                  aria-hidden="true"
                  className="h-[1.5px] w-[22px] shrink-0 rounded-full bg-[#3AB1F6]"
                />
                Two ways to get there
              </p>

              <div className="mt-[17.17px]">
                <p className={`${EYEBROW} text-[#0A0D1699]`}>
                  Traditional dictation
                </p>
                <div className="mt-[10px]">
                  <FlowSteps steps={TRADITIONAL_FLOW} />
                </div>
              </div>

              <div className="mt-[21.46px]">
                <p className={`${EYEBROW} text-[#3AB1F6]`}>AI voice typing</p>
                <div className="mt-[10px]">
                  <FlowSteps steps={AI_FLOW} accentArrows />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WritingWorkflowSection;
